// lib/kampfRegeln.js — Abschrift von backend/utils/kampfRegeln.js für den
// Browser (Zeitplan, Rechenhilfe der Turnier-Einstellungen, Schreiber-Panel).
//
// ⚠️ Nicht hier ändern, sondern im Backend und dann den Teil zwischen
// „RECHENKERN ANFANG“ und „RECHENKERN ENDE“ wörtlich herüberkopieren.
// backend/utils/__tests__/kampfRegeln.test.js vergleicht den Text und rechnet
// beide Dateien über ein Raster; laufen sie auseinander, fällt `npm test`.
//
// CommonJS, damit der Backend-Test die Datei laden kann; webpack nimmt sie genauso.

// ═══ RECHENKERN ANFANG ═══
const ZEILEN_SCHLUESSEL = ['Formen', 'Selbstverteidigung', 'Bruchtest', 'Pointfighting', 'Continous Fighting', 'K1', 'Fullcontact', 'Rumble', 'Kumite', 'Grappling'];
// person = je Starter, runden = Runden × Rundendauer, kampfdauer = eine
// Kampfzeit, rumble = bis zum dritten Gürtel, begegnung = unbekannte Disziplin
const ART = {
  Formen: 'person', Selbstverteidigung: 'person', Bruchtest: 'person',
  Pointfighting: 'runden', 'Continous Fighting': 'runden', K1: 'runden', Fullcontact: 'runden',
  Rumble: 'rumble', Kumite: 'kampfdauer', Grappling: 'kampfdauer', Default: 'begegnung',
};
const IST_KAMPF = (art) => art === 'runden' || art === 'kampfdauer' || art === 'rumble';
// Alt-Schlüssel (bis 23.10.2026), nur wenn die eigene Zeile fehlt
const ALT_ZEILEN = { 'Continous Fighting': ['Kickboxen'], K1: ['Kickboxen'], Fullcontact: ['Vollkontakt', 'Kickboxen'] };
// Feste Werte, wenn nichts gespeichert ist (wie bisher ZeitplanDashboard.calculateDuration)
const HART = {
  durationPerPerson: 3, breakAfter: 1, roundDuration: 2, rounds: 2, breakBetweenRounds: 1, durationPerMatch: 5,
  'verlängerungsDauer': 1, goldenPoint: false, erholungszeit: 0,
};
const UNTER_FELDER = ['rounds', 'roundDuration', 'breakBetweenRounds', 'matchDauer', 'durationPerMatch', 'verlängerungsDauer', 'goldenPoint', 'erholungszeit'];
const KIDS_BIS_ALTER = 12;
const RESERVE_STANDARD = 10;

/** Katalog-Disziplin (divlisttdaalle.Division_Type) → Zeilenschlüssel, sonst null. */
function disziplinAusTyp(typ) {
  const t = String(typ == null ? '' : typ).toLowerCase().replace(/[\s_-]+/g, '');
  if (!t) return null;
  if (t === 'formen' || t === 'kata' || t === 'forms') return 'Formen';
  if (t === 'selbstverteidigung' || t === 'selfdefense') return 'Selbstverteidigung';
  if (t === 'bruchtest' || t === 'breaking') return 'Bruchtest';
  if (t === 'pointfighting' || t === 'pointsparring') return 'Pointfighting';
  if (t === 'continousfighting' || t === 'continuousfighting') return 'Continous Fighting';
  if (t === 'k1') return 'K1';
  if (t === 'fullcontact' || t === 'vollkontakt') return 'Fullcontact';
  if (t === 'rumble') return 'Rumble';
  if (t === 'kumite') return 'Kumite';
  if (t === 'grappling') return 'Grappling';
  return null;
}

const leer = (v) => v === undefined || v === null || v === '';
const zahlOder = (v, sonst) => {
  if (leer(v)) return sonst;
  const n = Number(v);
  return Number.isFinite(n) ? n : sonst;
};

function optionenAus(disziplinen) {
  const o = (disziplinen && disziplinen._optionen) || {};
  const r = zahlOder(o.verlaengerungReserve, RESERVE_STANDARD);
  return {
    trennVorrundeFinale: !!o.trennVorrundeFinale,
    trennAltersgruppen: !!o.trennAltersgruppen,
    verlaengerungReserve: Math.max(0, Math.min(100, r)),
  };
}

/** Zeile, mit der gerechnet wird: eigene → Alt-Schlüssel → „Default“ → leer. */
function zeileAus(disziplinen, schluessel) {
  const d = disziplinen && typeof disziplinen === 'object' ? disziplinen : {};
  if (d[schluessel] && typeof d[schluessel] === 'object') return d[schluessel];
  for (const alt of ALT_ZEILEN[schluessel] || []) if (d[alt] && typeof d[alt] === 'object') return d[alt];
  return (d.Default && typeof d.Default === 'object') ? d.Default : {};
}

/**
 * Ein Feld, wie es für diesen Kampf gilt. Unterzeilen nur, wenn der Schalter
 * oben an ist: Kids · Finale → Kids → Finale → Grundzeile → fester Wert.
 * `breakAfter` (Pause nach dem Kampf) hat keine Unterzeile.
 */
function feldWert(disziplinen, schluessel, feld, lage) {
  const l = lage || {};
  const o = optionenAus(disziplinen);
  const z = zeileAus(disziplinen, schluessel);
  const finale = !!l.finale && o.trennVorrundeFinale;
  const kids = !!l.kids && o.trennAltersgruppen;
  const kette = [];
  if (UNTER_FELDER.includes(feld)) {
    if (kids && finale) kette.push(z.kids_finale);
    if (kids) kette.push(z.kids);
    if (finale) kette.push(z.finale);
  }
  kette.push(z);
  for (const q of kette) {
    if (q && typeof q === 'object' && !leer(q[feld])) return feld === 'goldenPoint' ? !!q[feld] : q[feld];
  }
  return HART[feld];
}

const zahlFeld = (d, k, f, l) => zahlOder(feldWert(d, k, f, l), zahlOder(HART[f], 0));

/** Ist die Altersklasse eine Kids-Klasse (F3)? */
function istKids(ageTo) {
  if (leer(ageTo)) return false;
  const n = Number(ageTo);
  return Number.isFinite(n) && n <= KIDS_BIS_ALTER;
}

/* ── Dauern mit Sekunden (25.10.2026) ──
   Gespeichert wird weiter in MINUTEN (wie bisher; alle Altwerte sind ganze
   Minuten), seit 25.10.2026 auch mit Bruchteil: 1:30 = 1.5, 1:20 = 1.3333.
   Gerechnet wird in ganzen Sekunden (Math.round(Minuten × 60)), damit 1.3333
   wieder genau 80 s ergibt. Eingabe: „1:30“, „90 s“, „1,5“, „1.5 min“, „2“. */
const sekAusMin = (m) => Math.max(0, Math.round(Number(m) * 60));

/** Eingabe → Sekunden, sonst null (Unsinn). Zahl ohne Einheit = Minuten. */
function dauerSekunden(eingabe) {
  if (typeof eingabe === 'number') return Number.isFinite(eingabe) && eingabe >= 0 ? sekAusMin(eingabe) : null;
  const t = String(eingabe == null ? '' : eingabe).trim().toLowerCase().replace(/\s+/g, ' ');
  if (!t) return null;
  let m = /^(\d{1,3}):([0-5]\d)(?: ?min)?$/.exec(t);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  m = /^(\d+(?:[.,]\d+)?) ?(s|sek|sek\.|sekunden?)$/.exec(t);
  if (m) return Math.round(Number(m[1].replace(',', '.')));
  m = /^(\d+(?:[.,]\d+)?) ?(m|min|min\.|minuten?)?$/.exec(t);
  if (m) return sekAusMin(Number(m[1].replace(',', '.')));
  return null;
}

/** Sekunden → Minuten zum Speichern (4 Stellen; zurück über Math.round genau). */
const minutenZumSpeichern = (sek) => Math.round((Number(sek) / 60) * 10000) / 10000;

/** Sekunden → „2“ (ganze Minuten) bzw. „1:30“. */
function dauerText(sek) {
  const n = Math.max(0, Math.round(Number(sek) || 0));
  if (n % 60 === 0) return String(n / 60);
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
}

/**
 * Alles, was für einen Kampf gilt, in Sekunden (…Sek) und Minuten (…Min,
 * dieselbe Zahl / 60, für Anzeigen).
 * lage: { finale, kids }
 */
function regelnFuer(disziplinen, schluessel, lage) {
  const k = ART[schluessel] ? schluessel : 'Default';
  const art = ART[k];
  const sek = (f) => sekAusMin(zahlFeld(disziplinen, k, f, lage));
  const runden = Math.max(1, Math.round(zahlFeld(disziplinen, k, 'rounds', lage)));
  const rundeSek = sek('roundDuration');
  const pauseSek = sek('breakBetweenRounds');
  const rahmenSek = runden * rundeSek + (runden - 1) * pauseSek;
  let kampfSek;
  let uhr = { runden, rundeSek, pauseSek };
  if (art === 'kampfdauer') {
    const m = feldWert(disziplinen, k, 'matchDauer', lage);
    if (!leer(m) && Number.isFinite(Number(m))) {
      kampfSek = sekAusMin(m);
      uhr = { runden: 1, rundeSek: kampfSek, pauseSek: 0 };
    } else {
      kampfSek = rahmenSek; // noch keine Kampfdauer gespeichert: wie bisher Runden × Dauer
    }
  } else if (art === 'rumble') {
    kampfSek = Math.min(sek('durationPerMatch'), rahmenSek);
  } else if (art === 'runden') {
    kampfSek = rahmenSek;
  } else if (art === 'begegnung') {
    kampfSek = sek('durationPerMatch');
  } else {
    kampfSek = sek('durationPerPerson');
  }
  const breakAfterSek = sek('breakAfter');
  const verlaengerungSek = sek('verlängerungsDauer');
  const erholungSek = sek('erholungszeit');
  return {
    schluessel: k, art, istKampf: IST_KAMPF(art),
    kampfSek, rahmenSek, breakAfterSek, verlaengerungSek, erholungSek,
    kampfMin: kampfSek / 60, rahmenMin: rahmenSek / 60, breakAfterMin: breakAfterSek / 60,
    verlaengerungMin: verlaengerungSek / 60, erholungMin: erholungSek / 60,
    uhr: { ...uhr, rundeMin: uhr.rundeSek / 60, pauseMin: uhr.pauseSek / 60 },
    goldenPoint: !!feldWert(disziplinen, k, 'goldenPoint', lage),
  };
}

/** Kampfuhr in Sekunden (Schreiber-Panel, Kampfrichter-App). */
function uhrFuer(disziplinen, schluessel, lage) {
  const r = regelnFuer(disziplinen, schluessel, lage);
  return {
    schluessel: r.schluessel, istKampf: r.istKampf,
    runden: r.uhr.runden, rundeSek: r.uhr.rundeSek, pauseSek: r.uhr.pauseSek,
    verlaengerungSek: r.verlaengerungSek, goldenPoint: r.goldenPoint,
    erholungMin: r.erholungMin,
    // Rumble: Kampf endet mit dem dritten Gürtel, die Uhr ist nur der Rahmen (F2)
    endetMitGuertel: r.art === 'rumble' ? 3 : null,
    finale: !!(lage && lage.finale), kids: !!(lage && lage.kids),
  };
}

/** Hat ein Turnier mit diesem Modus einen Kampf um Platz 3? (turniere.default_tournament_mode;
    ohne Angabe der Vorschlag der Eingabemaske, single_elimination_bronze) */
function platzDreiImModus(modus) {
  return (modus || 'single_elimination_bronze') === 'single_elimination_bronze';
}

/** Wie viele K.O.-Runden durchläuft der Finalist? */
function baumRunden(starter) {
  const n = Math.max(2, Math.floor(Number(starter) || 0));
  return Math.ceil(Math.log2(n));
}

/**
 * Minuten, die der Zeitplan für eine Kategorie ansetzt (ganze Minuten,
 * aufgerundet), mit den Teilen für die Rechenhilfe (…Sek genau, sonst Minuten).
 * lage: { starter, kids, platzDrei }
 * platzDrei: das Bracket hat einen Kampf um Platz 3 (Turniermodus
 * single_elimination_bronze). Er entsteht erst ab 4 Startern; bei 3 ist der
 * Halbfinal-Verlierer kampflos Dritter.
 */
function kategorieMinuten(disziplinen, schluessel, lage) {
  const l = lage || {};
  const n = Math.max(1, Math.floor(Number(l.starter) || 1));
  const vor = regelnFuer(disziplinen, schluessel, { kids: l.kids, finale: false });
  const fertig = (o) => ({ ...o, minuten: Math.ceil(o.gesamtSek / 60) });
  if (vor.art === 'person' || vor.art === 'begegnung') {
    const k = vor.art === 'person' ? n : Math.max(1, Math.ceil(n / 2));
    const gesamtSek = k * (vor.kampfSek + vor.breakAfterSek);
    return fertig({ art: vor.art, schluessel: vor.schluessel, starter: n, kaempfe: k,
      je: vor.kampfMin, pause: vor.breakAfterMin, jeSek: vor.kampfSek, pauseSek: vor.breakAfterSek,
      summe: gesamtSek / 60, gesamtSek });
  }
  const fin = regelnFuer(disziplinen, schluessel, { kids: l.kids, finale: true });
  const platzDrei = !!l.platzDrei && n >= 4 ? 1 : 0;
  const k = Math.max(1, n - 1) + platzDrei;
  const p = vor.breakAfterSek;
  // Platz 3 rechnet mit den Vorrundenwerten (F4: Finale nur der Endkampf)
  const summeSek = (k - 1) * (vor.kampfSek + p) + (fin.kampfSek + p);
  // Erholung (F7): der Finalist kämpft baumRunden-mal; zwischen zwei eigenen
  // Kämpfen mindestens die Erholungszeit
  const r = baumRunden(n);
  const e = vor.erholungSek;
  const mindestSek = e > 0 && n >= 2 ? (r - 1) * (vor.kampfSek + e) + fin.kampfSek + p : 0;
  const ablaufSek = Math.max(summeSek, mindestSek);
  // Verlängerungs-Reserve (F12)
  const prozent = optionenAus(disziplinen).verlaengerungReserve;
  const reserveKaempfe = n >= 2 && prozent > 0 ? Math.ceil((k * prozent) / 100) : 0;
  const reserveSek = reserveKaempfe * vor.verlaengerungSek;
  return fertig({
    art: vor.art, schluessel: vor.schluessel, starter: n, kaempfe: k, platzDrei,
    je: vor.kampfMin, jeFinale: fin.kampfMin, pause: p / 60,
    jeSek: vor.kampfSek, jeFinaleSek: fin.kampfSek, pauseSek: p,
    summe: summeSek / 60, summeSek, baumRunden: r, erholung: e / 60, erholungSek: e,
    mindest: mindestSek / 60, mindestSek, durchErholung: mindestSek > summeSek,
    reserveProzent: prozent, reserveKaempfe, reserveJe: vor.verlaengerungMin, reserveJeSek: vor.verlaengerungSek,
    reserve: reserveSek / 60, reserveSek,
    gesamtSek: ablaufSek + reserveSek,
  });
}

/**
 * Gleichstand am Ende der Kampfzeit: was jetzt? (F5, F6)
 * verlaengerungen = bisher in diesem Kampf gelaufene Verlängerungen.
 */
function gleichstandAblauf(regeln, verlaengerungen) {
  const n = Math.max(0, Math.floor(Number(verlaengerungen) || 0));
  if (n >= 1) return 'kampfrichterentscheid';
  // Verlängerung auf 0 gestellt: gleich die Kampfrichter
  if (regeln && (regeln.verlaengerungSek === 0 || regeln.verlaengerungMin === 0)) return 'kampfrichterentscheid';
  return regeln && regeln.goldenPoint ? 'golden_point' : 'verlaengerung';
}
// ═══ RECHENKERN ENDE ═══

module.exports = {
  ZEILEN_SCHLUESSEL, ART, ALT_ZEILEN, HART, KIDS_BIS_ALTER, RESERVE_STANDARD,
  disziplinAusTyp, optionenAus, zeileAus, feldWert, istKids, regelnFuer, uhrFuer,
  baumRunden, kategorieMinuten, gleichstandAblauf, dauerSekunden, dauerText, minutenZumSpeichern, platzDreiImModus,
};
